import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Box, Paper, Accordion, AccordionSummary, AccordionDetails, List, ListItem, Button, Link as MuiLink } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';

const DWGViewer = ({ onAreaSelect, selectedArea }) => {
  const canvasRef = useRef(null);
  
  const predefinedAreas = [
    { id: 'dock1', name: 'Dock 1', x: 50, y: 80, width: 200, height: 150 },
    { id: 'dock2', name: 'Dock 2', x: 300, y: 80, width: 200, height: 150 },
    { id: 'dock3', name: 'Dock 3', x: 550, y: 80, width: 200, height: 150 },
    { id: 'welding', name: 'Welding Shop', x: 50, y: 280, width: 150, height: 100 },
    { id: 'assembly', name: 'Assembly Hall', x: 250, y: 280, width: 150, height: 100 },
    { id: 'paint', name: 'Paint Shop', x: 450, y: 280, width: 150, height: 100 },
    { id: 'storage', name: 'Storage', x: 650, y: 280, width: 100, height: 100 },
    { id: 'office', name: 'Office', x: 50, y: 420, width: 120, height: 80 },
    { id: 'crane', name: 'Crane Rail', x: 50, y: 245, width: 700, height: 10 },
    { id: 'harbor', name: 'Harbor Water', x: 50, y: 520, width: 700, height: 60 }
  ];

  const drawSelectedAreas = useCallback((ctx) => {
    if (selectedArea) {
      const area = predefinedAreas.find(a => a.id === selectedArea);
      if (area) {
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 3;
        ctx.strokeRect(area.x, area.y, area.width, area.height);
        ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
        ctx.fillRect(area.x, area.y, area.width, area.height);
      }
    }
  }, [selectedArea]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      drawSelectedAreas(ctx);
    };
    img.src = '/shipyard-plan.svg';
  }, [selectedArea, drawSelectedAreas]);


  const getMousePos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleClick = (e) => {
    const pos = getMousePos(e);
    
    const clickedArea = predefinedAreas.find(area => 
      pos.x >= area.x && pos.x <= area.x + area.width &&
      pos.y >= area.y && pos.y <= area.y + area.height
    );
    
    if (clickedArea) {
      onAreaSelect(clickedArea);
    }
  };

  return (
    <Box sx={{ border: '1px solid #ccc', display: 'inline-block' }}>
      <canvas
        ref={canvasRef}
        width={800}
        height={600}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      />
    </Box>
  );
};

const FacilityDetails = () => {
  const [selectedArea, setSelectedArea] = useState(null);
  
  const getProjectIdFromShipName = (shipName) => {
    const projectMap = {
      'Ship Alpha': '1',
      'Ship Beta': '2', 
      'Ship Gamma': '3'
    };
    return projectMap[shipName] || '1';
  };
  
  const parseUnitString = (unitString) => {
    const parts = unitString.split(' - ');
    return {
      unitName: parts[0],
      shipName: parts[1],
      projectId: getProjectIdFromShipName(parts[1])
    };
  };
  
  const generateUnits = () => {
    const ships = ['Ship Alpha', 'Ship Beta', 'Ship Gamma'];
    const units = [];
    for (let i = 10; i <= 50; i++) {
      const randomShip = ships[Math.floor(Math.random() * ships.length)];
      units.push(`UNIT${i} - ${randomShip}`);
    }
    return units;
  };
  
  const allAreas = [
    { id: 'dock1', name: 'Dock 1', units: generateUnits().slice(0, 3) },
    { id: 'dock2', name: 'Dock 2', units: generateUnits().slice(3, 5) },
    { id: 'dock3', name: 'Dock 3', units: generateUnits().slice(5, 7) },
    { id: 'welding', name: 'Welding Shop', units: generateUnits().slice(7, 9) },
    { id: 'assembly', name: 'Assembly Hall', units: generateUnits().slice(9, 12) },
    { id: 'paint', name: 'Paint Shop', units: generateUnits().slice(12, 14) },
    { id: 'storage', name: 'Storage', units: generateUnits().slice(14, 15) },
    { id: 'office', name: 'Office', units: [] },
    { id: 'crane', name: 'Crane Rail', units: [] },
    { id: 'harbor', name: 'Harbor Water', units: generateUnits().slice(15, 17) }
  ];

  const handleAreaSelect = (area) => {
    setSelectedArea(selectedArea === area.id ? null : area.id);
  };
  
  const handleAreaClick = (areaId) => {
    setSelectedArea(selectedArea === areaId ? null : areaId);
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Shipyard Gdansk Details
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, height: '100vh' }}>
        <Paper sx={{ p: 2, width: '50%' }}>
          <Typography variant="h6" gutterBottom>
            Interactive Plan Viewer
          </Typography>
          <DWGViewer onAreaSelect={handleAreaSelect} selectedArea={selectedArea} />
        </Paper>
        
        <Paper sx={{ p: 2, width: '50%' }}>
          <Typography variant="h6" gutterBottom>
            Facility Areas
          </Typography>
          {allAreas.map((area) => (
            <Accordion 
              key={area.id}
              expanded={selectedArea === area.id}
              onChange={() => handleAreaClick(area.id)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{area.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {area.units.length > 0 ? (
                  <List dense>
                    {area.units.map((unit, index) => {
                      const { unitName, projectId } = parseUnitString(unit);
                      return (
                        <ListItem key={index}>
                          <MuiLink 
                            component={Link}
                            to={`/projects/${projectId}/unit/${unitName}`}
                            sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {unit}
                          </MuiLink>
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No units assigned
                  </Typography>
                )}
                <Box sx={{ mt: 2 }}>
                  <Button 
                    component={Link} 
                    to={`/facilities/1/${area.id}`}
                    variant="outlined" 
                    size="small"
                  >
                    Show Facility Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Box>
    </div>
  );
};

export default FacilityDetails;